import { mkdir, writeFile } from "node:fs/promises";
import path from "node:path";
import type { ReviewAgent, ReviewInput, ReviewPlanner } from "./agent.js";
import { createReviewAgent, createReviewPlanner } from "./adapters.js";
import type { UiReviewConfig } from "./config.js";
import { writePullRequestDiff } from "./diff.js";
import { logger } from "./log.js";
import { runCommand, startCommand, terminate, type RunningProcess } from "./process.js";
import { renderMarkdown } from "./report.js";

const log = logger.child("run");

export interface RunOptions {
  cwd: string;
  config: UiReviewConfig;
  agent?: ReviewAgent;
  planner?: ReviewPlanner;
}

export async function runReview(options: RunOptions): Promise<void> {
  const outputDirectory = path.join(options.cwd, ".revuiw");
  const evidenceDirectory = path.join(outputDirectory, "screenshots");
  const appLogPath = path.join(outputDirectory, "app.log");
  const diffPath = path.join(outputDirectory, "pr.diff");
  await mkdir(evidenceDirectory, { recursive: true });

  let app: RunningProcess | undefined;
  let exitCode = 1;
  try {
    log.info(`Reviewing ${options.cwd}`);
    log.debug(`Output directory: ${outputDirectory}`);

    const installCommands = commands(options.config.install);
    if (installCommands.length) log.info(`Running ${installCommands.length} install command(s)`);
    for (const command of installCommands) {
      log.info(`$ ${command}`);
      await runCommand(command, options.cwd, appLogPath);
    }
    const setupCommands = commands(options.config.setup);
    if (setupCommands.length) log.info(`Running ${setupCommands.length} setup command(s)`);
    for (const command of setupCommands) {
      log.info(`$ ${command}`);
      await runCommand(command, options.cwd, appLogPath);
    }

    log.info("Collecting pull request diff");
    await writePullRequestDiff(options.cwd, diffPath, options.config.diff);
    const input: ReviewInput = {
      cwd: options.cwd,
      baseUrl: new URL(options.config.ready),
      pullRequestDiffPath: diffPath,
      evidenceDirectory,
      runtimeDirectory: outputDirectory,
      timeoutMs: options.config.timeoutMs,
      prompt: options.config.agents.reviewer.prompt,
    };

    const plannerConfig = options.config.agents.planner;
    if (options.planner || plannerConfig) {
      try {
        input.plan = await (options.planner ?? createReviewPlanner(plannerConfig!)).plan(input);
      } catch (error) {
        const message = error instanceof Error ? error.stack ?? error.message : String(error);
        await writeFile(path.join(outputDirectory, "planner-error.txt"), message);
        if (plannerConfig?.required !== false) throw error;
        log.warn(`Planning agent failed; continuing review: ${message}`);
      }
    } else {
      log.debug("No planner configured; skipping planning stage");
    }

    log.info(`Starting application: ${options.config.start}`);
    app = startCommand(options.config.start, options.cwd, appLogPath);
    void app.completion.catch(() => undefined);
    log.info(`Waiting for ${options.config.ready}`);
    await waitUntilReady(options.config.ready, options.config.timeoutMs, app);
    log.info("Application is ready");

    const result = await (options.agent ?? createReviewAgent(options.config.agents.reviewer)).review(input);
    await Promise.all([
      writeFile(path.join(outputDirectory, "result.json"), JSON.stringify(result, null, 2)),
      writeFile(path.join(outputDirectory, "comment.md"), renderMarkdown(result)),
    ]);
    log.info(`Review ${result.status.toUpperCase()} with ${result.findings.length} finding(s); wrote results to ${outputDirectory}`);
    exitCode = 0;
  } catch (error) {
    const message = error instanceof Error ? error.stack ?? error.message : String(error);
    log.error(`Review failed: ${message}`);
    await writeFile(path.join(outputDirectory, "comment.md"), failureReport(message));
    throw error;
  } finally {
    if (app) {
      log.debug("Stopping application");
      await terminate(app);
    }
    for (const command of commands(options.config.stop)) {
      log.info(`$ ${command}`);
      try { await runCommand(command, options.cwd, appLogPath); }
      catch (error) { log.warn(`Stop command failed: ${String(error)}`); }
    }
    await writeFile(path.join(outputDirectory, "exit-code"), `${exitCode}\n`);
  }
}

function commands(value: string | string[] | undefined): string[] {
  return value === undefined ? [] : Array.isArray(value) ? value : [value];
}

async function waitUntilReady(url: string, timeoutMs: number, app: RunningProcess): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (app.child.exitCode !== null || app.child.signalCode !== null) {
      throw new Error("Application exited before its ready URL responded");
    }
    try {
      const response = await fetch(url, { signal: AbortSignal.timeout(2_000) });
      if (response.status < 500) return;
    } catch {}
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  throw new Error(`Application was not ready at ${url} after ${timeoutMs}ms`);
}

function failureReport(message: string): string {
  return `## Autonomous UI review\n\n**Result:** Review infrastructure failed.\n\n\`\`\`text\n${message}\n\`\`\`\n`;
}
